import React from 'react';

import { useTranslation } from 'react-i18next';

import { Button, PageSizeType, Table } from 'design-system-web';
import { Loading } from 'components/general';
import { M, S } from 'components/typography';

import styles from './styles.scss';

type TeamRow = { name: string; groupRoleIds: string[] };

type Props = {
  teamRows: TeamRow[];
  teamsTotal: number;
  teamsLoading: boolean;
  teamsError: string | null;
  teamsKeyword: string;
  teamsPage: number;
  teamsPageSize: PageSizeType;
  selectedTeamNames: string[];
  channelCount: number;
  canAdvance: boolean;
  onTeamsKeywordChange: (keyword: string) => void;
  onTeamsPageChange: (page: number) => void;
  onTeamsPageSizeChange: (pageSize: PageSizeType) => void;
  onSelect: (names: string[]) => void;
  onNext: () => void;
  onClose: () => void;
};

export function TeamsSelectionStep({
  teamRows,
  teamsTotal,
  teamsLoading,
  teamsError,
  teamsKeyword,
  teamsPage,
  teamsPageSize,
  selectedTeamNames,
  channelCount,
  canAdvance,
  onTeamsKeywordChange,
  onTeamsPageChange,
  onTeamsPageSizeChange,
  onSelect,
  onNext,
  onClose,
}: Props) {
  const { t } = useTranslation();

  return (
    <div>
      <M mb={2}>
        {t(
          'web.admin.portalManagement.bulkTeamManagement.assignPermission.wizard.teamsStep.description',
          { channelCount }
        )}
      </M>

      {teamsLoading && (
        <div data-test="teamsSelectionLoading">
          <Loading />
        </div>
      )}

      {teamsError && <S data-test="teamsSelectionError">{teamsError}</S>}

      {!teamsLoading && !teamsError && (
        <>
          <Table
            isSelectable
            columns={[
              {
                key: 'name',
                header: t(
                  'web.admin.portalManagement.bulkTeamManagement.assignPermission.wizard.teamsStep.column.name'
                ),
              },
            ]}
            data={teamRows}
            isLoading={false}
            totalRows={teamsTotal}
            pagination="server"
            hasKeywordFilter
            keywordFilter={teamsKeyword}
            onKeywordFilterChange={onTeamsKeywordChange}
            page={teamsPage}
            pageSize={teamsPageSize}
            onPageChange={onTeamsPageChange}
            onPageSizeChange={onTeamsPageSizeChange}
            rowSelectionExternal
            rowSelection={selectedTeamNames}
            getRowId={(row: TeamRow) => row.name}
            onSelectionChange={onSelect}
            tableKey="bulkTeamManagement.wizardTeamsTable"
          />
          <S>
            {t(
              'web.admin.portalManagement.bulkTeamManagement.assignPermission.wizard.selectedCount',
              {
                count: selectedTeamNames.length,
                total: teamsTotal,
              }
            )}
          </S>
        </>
      )}

      <div className={styles.footer}>
        <Button
          variant="secondary"
          onClick={onClose}
          testId="wizardCloseButton"
        >
          {t(
            'web.admin.portalManagement.bulkTeamManagement.assignPermission.wizard.close'
          )}
        </Button>
        <Button
          disabled={!canAdvance || teamsLoading || Boolean(teamsError)}
          onClick={onNext}
          testId="wizardContinueButton"
        >
          {t(
            'web.admin.portalManagement.bulkTeamManagement.assignPermission.wizard.continue'
          )}
        </Button>
      </div>
    </div>
  );
}
